import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Cookie, X, ArrowRight } from 'lucide-react';

const CONSENT_KEY = 'ng_cookie_consent';

const hasConsent = () => {
  try {
    if (localStorage.getItem(CONSENT_KEY) === '1') return true;
  } catch (e) { /* ignore */ }
  return typeof document !== 'undefined' && document.cookie.indexOf(CONSENT_KEY + '=1') !== -1;
};

/*
 * Cookie-consent badge shown at the bottom of every page of the marketing site.
 * Standard behaviour: appears once, "Accept" (or X) stores consent for a year,
 * and it never bothers the visitor again on any page. Not rendered during
 * react-snap prerender, so it stays out of the static HTML / SEO snapshot.
 */
export const CookieBanner = () => {
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false); // controls slide-in animation

  useEffect(() => {
    // Never surface during static prerender (react-snap sets this UA).
    if (typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent)) return;
    if (hasConsent()) return;
    setVisible(true);
    const t = setTimeout(() => setShown(true), 60); // trigger transition after mount
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    try {
      document.cookie = CONSENT_KEY + '=1; max-age=31536000; path=/; SameSite=Lax';
      localStorage.setItem(CONSENT_KEY, '1');
    } catch (e) { /* ignore */ }
    setShown(false);
    setTimeout(() => setVisible(false), 250);
  };

  if (!visible) return null;
  const ru = language === 'ru';
  const cookieHref = ru ? '/ru/cookie' : '/cookie';

  return (
    <div
      className="fixed z-[9999] left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-sm"
      style={{
        transform: shown ? 'translateY(0)' : 'translateY(140%)',
        opacity: shown ? 1 : 0,
        transition: 'transform .35s cubic-bezier(.22,1,.36,1), opacity .35s ease',
      }}
      role="dialog"
      aria-label={ru ? 'Уведомление о файлах cookie' : 'Cookie notice'}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 border border-gray-100 p-5 sm:p-6">
        <button
          type="button"
          onClick={accept}
          aria-label={ru ? 'Закрыть' : 'Close'}
          className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <span className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-6 h-6 text-blue-600" />
          </span>
          <img src="/logo-horizontal-dark.svg" alt="NetGrid Host" className="h-5 w-auto" />
        </div>

        <p className="text-sm text-gray-600 leading-relaxed mb-4 pr-4">
          {ru
            ? 'Мы используем файлы cookie для корректной работы сайта, аналитики и улучшения вашего опыта. Продолжая пользоваться сайтом, вы соглашаетесь с их использованием.'
            : 'We use cookies to keep the site working, for analytics, and to improve your experience. By continuing to use the site, you agree to their use.'}
        </p>

        <div className="flex items-center justify-between gap-3">
          <a
            href={cookieHref}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            {ru ? 'Политика Cookie' : 'Cookie Policy'}
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
          <button
            type="button"
            onClick={accept}
            className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
          >
            {ru ? 'Принять' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};
